// People & organisations stream contracts (ADR-028): organisation register and dated affiliations
// (PB-03-03), recorded person link review (PB-03-04), host reassignment decisions (PB-03-02), group
// roster import into a visit (PB-04-03) and the data-minimisation collection matrix (PB-16-03).
// Messages are @passage/i18n keys (PD-015).
import { z } from 'zod';

const Uuid = z.uuid();
const Version = z.number().int().min(1);
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const Reason = z.string().trim().min(3, 'orgs.v.reason').max(500, 'v.tooLong');
const Email = z
  .string()
  .trim()
  .max(254, 'v.tooLong')
  .transform((v) => (v === '' ? null : v))
  .pipe(z.email('v.email').nullable())
  .nullable()
  .optional();
const Phone = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v))
  .pipe(z.string().regex(/^\+?[0-9 ()-]{6,24}$/, 'v.phone').nullable())
  .nullable()
  .optional();

// ------------------------------------------------------------------ organisations (PB-03-03)
export const ORGANISATION_KINDS = ['visitor', 'contractor', 'vendor', 'delivery', 'government', 'other'] as const;
export type OrganisationKind = (typeof ORGANISATION_KINDS)[number];

export const OrganisationListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  kind: z.enum(ORGANISATION_KINDS).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  after: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const CreateOrganisationRequest = z.object({
  name: trimmed(200),
  legalName: optionalText(200),
  kind: z.enum(ORGANISATION_KINDS).default('visitor'),
  sponsorHostId: Uuid.nullable().optional(),
});

export const UpdateOrganisationRequest = z.object({
  expectedVersion: Version,
  name: trimmed(200).optional(),
  legalName: optionalText(200),
  kind: z.enum(ORGANISATION_KINDS).optional(),
  sponsorHostId: Uuid.nullable().optional(),
  /** Required when the display or legal name changes (kept in the name history). */
  reason: optionalText(500),
});

export const OrganisationStatusRequest = z.object({ expectedVersion: Version, reason: Reason });

export interface OrganisationSummaryDto {
  readonly id: string;
  readonly name: string;
  readonly legalName: string | null;
  readonly kind: OrganisationKind;
  readonly status: 'active' | 'inactive';
  /** Linked to a contractor company: name, type and status are governed by the contractor register. */
  readonly contractorGoverned: boolean;
  readonly version: number;
}

export interface OrganisationContactDto {
  readonly id: string;
  readonly name: string;
  readonly roleTitle: string | null;
  /** Masked unless the caller manages the organisation register. */
  readonly email: string | null;
  readonly phone: string | null;
  readonly masked: boolean;
  readonly isPrimary: boolean;
  readonly status: 'active' | 'inactive';
  readonly version: number;
}

export interface OrganisationNameChangeDto {
  readonly previousName: string;
  readonly newName: string;
  readonly previousLegalName: string | null;
  readonly newLegalName: string | null;
  readonly changedBy: string | null;
  readonly reason: string | null;
  readonly changedAt: string;
}

export interface OrganisationDetailDto extends OrganisationSummaryDto {
  readonly sponsor: { readonly id: string; readonly displayName: string; readonly status: 'active' | 'inactive' } | null;
  readonly statusReason: string | null;
  readonly statusChangedAt: string | null;
  readonly contractor: { readonly companyId: string; readonly status: string } | null;
  readonly contacts: readonly OrganisationContactDto[];
  readonly nameHistory: readonly OrganisationNameChangeDto[];
  readonly createdAt: string;
  readonly can: { readonly manage: boolean; readonly manageAffiliations: boolean };
}

export const CreateOrganisationContactRequest = z.object({
  name: trimmed(200),
  roleTitle: optionalText(120),
  email: Email,
  phone: Phone,
  isPrimary: z.boolean().default(false),
});
export const UpdateOrganisationContactRequest = z.object({
  expectedVersion: Version,
  name: trimmed(200).optional(),
  roleTitle: optionalText(120),
  email: Email,
  phone: Phone,
  isPrimary: z.boolean().optional(),
  status: z.enum(['active', 'inactive']).optional(),
});

/** Historical attribution (edge test): each visit keeps the organisation name it was recorded with. */
export interface OrganisationVisitDto {
  readonly visitId: string;
  readonly reference: string;
  readonly siteId: string;
  readonly startsAt: string | null;
  readonly planningStatus: string;
  readonly attributedAs: readonly string[];
  readonly participants: number;
}
export interface OrganisationVisitsResponse {
  readonly items: readonly OrganisationVisitDto[];
  readonly nextCursor: string | null;
  /** Some attributed visits are at sites outside the caller's scope (not listed). */
  readonly partialScope: boolean;
}

// ------------------------------------------------------------------ affiliations (PB-03-03)
export const CreatePersonAffiliationRequest = z.object({
  organisationId: Uuid,
  roleTitle: optionalText(120),
  startsOn: LocalDate,
  endsOn: LocalDate.nullable().optional(),
});
export const EndPersonAffiliationRequest = z.object({ expectedVersion: Version, endsOn: LocalDate, reason: Reason });

export interface AffiliationDto {
  readonly id: string;
  readonly person: { readonly id: string; readonly displayName: string } | null;
  readonly organisation: { readonly id: string; readonly name: string };
  readonly roleTitle: string | null;
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly endReason: string | null;
  readonly current: boolean;
  /** 'register' = this module; 'contractor_register' = worker affiliation governed by module 09 (read-only). */
  readonly source: 'register' | 'contractor_register';
  readonly version: number;
}
export interface AffiliationListResponse {
  readonly items: readonly AffiliationDto[];
  /** Affiliations of people the caller may not see exist and are not listed (PB-03-05 pattern). */
  readonly partialScope: boolean;
}

// ------------------------------------------------------------------ person links (PB-03-04)
export const MATCH_REASONS = ['email', 'phone', 'name'] as const;
export interface PossibleMatchDto {
  readonly id: string;
  readonly displayName: string;
  readonly organisation: string | null;
  readonly maskedEmail: string | null;
  readonly maskedPhone: string | null;
  readonly reasons: ReadonlyArray<(typeof MATCH_REASONS)[number]>;
  /** Shown instead of a linked duplicate whose primary profile the caller can see. */
  readonly linkedProfiles: number;
  readonly version: number;
}
export interface PossibleMatchesResponse {
  readonly person: { readonly id: string; readonly version: number };
  readonly items: readonly PossibleMatchDto[];
  readonly canDecide: boolean;
}

export const CreatePersonLinkRequest = z.object({
  otherPersonId: Uuid,
  decision: z.enum(['linked', 'kept_separate']),
  reason: Reason,
  expectedVersion: Version,
  otherExpectedVersion: Version,
});
export const ReversePersonLinkRequest = z.object({ reason: Reason });

export interface PersonLinkDto {
  readonly id: string;
  /** The primary profile of a "linked" decision. */
  readonly primary: { readonly id: string; readonly displayName: string };
  readonly other: { readonly id: string; readonly displayName: string };
  readonly decision: 'linked' | 'kept_separate';
  readonly matchReasons: readonly string[];
  readonly reason: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string;
  readonly status: 'active' | 'reversed';
  readonly reversedBy: string | null;
  readonly reversedAt: string | null;
  readonly reverseReason: string | null;
}
export interface PersonLinksResponse {
  readonly items: readonly PersonLinkDto[];
  readonly canDecide: boolean;
}

// ------------------------------------------------------------------ host reassignment (PB-03-02)
export const ReassignHostRequest = z.object({ toHostId: Uuid, reason: Reason, confirmImpact: z.boolean().default(false) });
export interface ReassignHostResponse {
  readonly reassignmentId: string;
  readonly visitId: string;
  readonly fromHostId: string;
  readonly toHostId: string;
  readonly approvalsInvalidated: number;
}

// ------------------------------------------------------------------ roster import (PB-04-03)
export const RosterFileRequest = z.object({
  fileName: z.string().trim().min(1).max(200),
  /** Base64 of the CSV (UTF-8) or XLSX file. */
  contentBase64: z.string().min(4).max(8_000_000),
});
export const CreateRosterImportRequest = RosterFileRequest.extend({
  /** Source column header → target field key ("" or absent = ignored). */
  mapping: z.record(z.string().max(200), z.string().max(60)),
});
export interface RosterFieldDto {
  readonly key: string;
  readonly label: string;
  readonly required: boolean;
}
export interface InspectRosterResponse {
  readonly format: 'csv' | 'xlsx';
  readonly headers: readonly string[];
  /** Columns that can never be imported: credentials/secrets and items this visit type may not collect. */
  readonly blockedHeaders: readonly string[];
  readonly suggestedMapping: Readonly<Record<string, string>>;
  readonly rowCount: number;
  readonly fields: readonly RosterFieldDto[];
}
export interface RosterMatchDto {
  readonly id: string;
  readonly label: string;
  readonly detail: string | null;
  readonly reasons: readonly string[];
}
export const ROSTER_ROW_STATUSES = ['valid', 'invalid', 'duplicate', 'added', 'failed', 'skipped'] as const;
export interface RosterRowDto {
  readonly rowNumber: number;
  readonly data: Readonly<Record<string, string>>;
  readonly status: (typeof ROSTER_ROW_STATUSES)[number];
  readonly errors: Readonly<Record<string, string>>;
  readonly matches: readonly RosterMatchDto[];
  readonly decision: 'create' | 'use_existing' | 'skip' | null;
  readonly decisionTarget: string | null;
  readonly participationId: string | null;
}
export interface RosterImportDto {
  readonly id: string;
  readonly visitId: string;
  readonly status: 'validated' | 'applying' | 'applied' | 'discarded';
  readonly fileName: string;
  readonly format: 'csv' | 'xlsx';
  readonly headers: readonly string[];
  readonly ignoredHeaders: readonly string[];
  readonly mapping: Readonly<Record<string, string>>;
  readonly applyPolicy: 'all_or_nothing' | 'valid_rows_only' | null;
  readonly counts: {
    readonly rows: number;
    readonly valid: number;
    readonly invalid: number;
    readonly duplicate: number;
    readonly undecided: number;
    readonly added: number;
    readonly failed: number;
    readonly skipped: number;
  };
  readonly createdAt: string;
  readonly appliedAt: string | null;
  readonly version: number;
}
export const RosterRowsQuery = z.object({
  status: z.enum([...ROSTER_ROW_STATUSES, 'all']).default('all'),
  after: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const DecideRosterRowRequest = z.object({ decision: z.enum(['create', 'use_existing', 'skip']), targetId: Uuid.nullable().optional() });
export const ApplyRosterRequest = z.object({ expectedVersion: Version, policy: z.enum(['all_or_nothing', 'valid_rows_only']) });
export const DiscardRosterRequest = z.object({ expectedVersion: Version });
export interface ApplyRosterResponse {
  readonly import: RosterImportDto;
  readonly added: number;
  readonly failed: number;
  readonly skipped: number;
}

// ------------------------------------------------------------------ collection matrix (PB-16-03)
export const COLLECTION_MODE_VALUES = ['required', 'optional', 'disallowed'] as const;
const ItemKey = z.string().trim().regex(/^(email|phone|organisation|photo|id_document|id_number|field:[a-z][a-z0-9_]{0,39}|document:[a-z][a-z0-9_-]{0,39})$/, 'orgs.v.item');
const CategoryOrAll = z.string().trim().regex(/^(\*|[a-z][a-z0-9_-]{0,39})$/, 'v.invalid');
export const CollectionRuleSchema = z.object({
  category: CategoryOrAll,
  purpose: z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.invalid').nullable().optional(),
  item: ItemKey,
  mode: z.enum(COLLECTION_MODE_VALUES),
});
export const CollectionMatrixDocumentSchema = z.object({ schema: z.literal(1), rules: z.array(CollectionRuleSchema).max(2000) });
export const SaveCollectionMatrixDraftRequest = z.object({ expectedVersion: z.number().int().min(0), document: CollectionMatrixDocumentSchema, note: optionalText(500) });
export const CollectionMatrixVersionRequest = z.object({ expectedVersion: Version });

export interface CollectionMatrixVersionDto {
  readonly id: string;
  readonly number: number;
  readonly status: 'draft' | 'published' | 'superseded' | 'discarded';
  readonly document: z.infer<typeof CollectionMatrixDocumentSchema>;
  readonly note: string | null;
  readonly publishedAt: string | null;
  readonly version: number;
}
export interface CollectionItemDto {
  readonly item: string;
  readonly label: string;
  readonly kind: 'builtin' | 'field' | 'document';
  /** Retention record class the values fall under (privacy registry, PB-16-04). */
  readonly recordClass: string | null;
  /** Never carried by visit, pre-registration or kiosk payloads; identity verification captures it. */
  readonly captureOnly: boolean;
}
export interface CollectionMatrixResponse {
  readonly published: CollectionMatrixVersionDto | null;
  readonly draft: CollectionMatrixVersionDto | null;
  readonly items: readonly CollectionItemDto[];
  readonly categories: ReadonlyArray<{ readonly code: string; readonly label: string; readonly documents: readonly string[] }>;
  /** Effective modes per category ('*' included) under the published matrix: category → item → mode. */
  readonly effective: Readonly<Record<string, Readonly<Record<string, { readonly mode: (typeof COLLECTION_MODE_VALUES)[number]; readonly source: 'matrix' | 'default' }>>>>;
  readonly canManage: boolean;
}
export const EffectiveCollectionQuery = z.object({
  siteId: Uuid,
  categoryCode: z.string().trim().max(40).optional(),
  purposeCode: z.string().trim().max(40).optional(),
});
export interface EffectiveCollectionResponse {
  readonly categoryCode: string | null;
  readonly purposeCode: string | null;
  readonly items: Readonly<Record<string, { readonly mode: (typeof COLLECTION_MODE_VALUES)[number]; readonly source: 'matrix' | 'default' }>>;
}
